import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// =====================================================
// SINCRONIZAÇÃO DE IMAGENS - VARIAÇÕES
// =====================================================

const VARIANT_IMAGE_MAP = {
  'Discovery pack of 24': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Discovery_24.jpg?v=1778620239',
  'Discovery pack of 8': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Discovery_24.jpg?v=1778620239',
  'Sleep pack of 24': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Sleep_24.jpg?v=1778620240',
  'Sleep pack of 8': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Sleep_8.jpg?v=1778620239',
  'Energy pack of 8': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Energy_8.jpg?v=1778620239',
  'Energy pack of 24': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Energy_24.jpg?v=1778620239',
  'Focus pack of 8': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Focus_8.jpg?v=1778620239',
  'Focus pack of 24': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Focus_24.jpg?v=1778620239',
  'Relax pack of 8': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Relax_8.jpg?v=1778620239',
  'Relax pack of 24': 'https://cdn.shopify.com/s/files/1/0708/5752/3289/files/Relax_24.jpg?v=1778620239'
};

function getProductType() {
  try {
    const title = (document.querySelector('h1') || document.querySelector('[class*="title"]') || {}).textContent.toLowerCase();
    if (title.includes('sleep')) return 'Sleep';
    if (title.includes('energy')) return 'Energy';
    if (title.includes('focus')) return 'Focus';
    if (title.includes('relax')) return 'Relax';
    if (title.includes('discovery')) return 'Discovery';
  } catch (e) {
    console.warn('[VariantSync] Erro ao detectar tipo:', e);
  }
  return '';
}

function getSelectedVariantName() {
  try {
    const btn = document.querySelector('button[aria-pressed="true"], button.selected, button.active');
    if (btn) {
      const text = btn.textContent.trim();
      const pack = text.includes('24') ? 'of 24' : 'of 8';
      const type = getProductType();
      return type ? `${type} pack ${pack}` : '';
    }
  } catch (e) {
    console.warn('[VariantSync] Erro ao detectar variante:', e);
  }
  return '';
}

function updateProductImage(variantName) {
  try {
    if (!variantName) return;

    const imageUrl = VARIANT_IMAGE_MAP[variantName];
    if (!imageUrl) {
      console.warn('[VariantSync] Variante não encontrada:', variantName);
      return;
    }

    const selectors = [
      'img.product-image',
      'img.product-featured-image',
      'img[data-product-featured-image]',
      '.product-photo img',
      '.product__image img',
      '.productImageContainer img',
      '.product-main-image img',
      '.product__media img:first-of-type',
      'picture img',
      '.media-gallery__item img',
      '.product-gallery__main-pdp img'
    ];

    for (const selector of selectors) {
      const img = document.querySelector(selector);
      if (img && img.tagName === 'IMG') {
        img.style.transition = 'opacity 0.3s ease-in-out';
        img.style.opacity = '0.7';
        
        setTimeout(() => {
          img.src = imageUrl;
          if (img.srcset) {
            img.srcset = imageUrl;
          }
          img.style.opacity = '1';
          console.log('[VariantSync] ✅ Imagem atualizada:', variantName);
        }, 100);
        
        return;
      }
    }
    
    console.warn('[VariantSync] Elemento de imagem não encontrado');
  } catch (e) {
    console.error('[VariantSync] Erro ao atualizar imagem:', e);
  }
}

// Inicializa sincronização ao carregar
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      const variantName = getSelectedVariantName();
      if (variantName) {
        updateProductImage(variantName);
      }
    }, 500);
  });
} else {
  setTimeout(() => {
    const variantName = getSelectedVariantName();
    if (variantName) {
      updateProductImage(variantName);
    }
  }, 500);
}

// Listener para cliques em botões
document.addEventListener('click', (e) => {
  try {
    const button = e.target.closest('button');
    if (button && (button.textContent.includes('Pack') || button.textContent.includes('8') || button.textContent.includes('24'))) {
      setTimeout(() => {
        const variantName = getSelectedVariantName();
        if (variantName) {
          console.log('[VariantSync] Botão clicado:', variantName);
          updateProductImage(variantName);
        }
      }, 200);
    }
  } catch (e) {
    console.warn('[VariantSync] Erro no listener de clique:', e);
  }
});

console.log('[VariantSync] ✅ Script de sincronização carregado');

// =====================================================
// CÓDIGO ORIGINAL DO PRODUTO
// =====================================================

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const quantitySelector = form.querySelector('quantity-selector-component');
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = this.querySelector('input[name="id"]');
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      const quantitySelector = this.querySelector('quantity-selector-component');
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  #onCartUpdate = async (event) => {
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const { variantId } = this.refs;

    if (!variantId.value) {
      this.#onSubmitError('Please select an option');
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    this.#disableForm();

    const body = new FormData(this);

    try {
      const response = await fetch(fetchConfig('add_to_cart'));

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.description);
      }

      const json = await response.json();

      const cartAddedEvent = new CustomEvent(ThemeEvents.cartAdd, {
        detail: {
          source: 'product-form-component',
          sourceId: this.id,
          data: json,
          resource: json.items[0] || null
        }
      });
      document.dispatchEvent(cartAddedEvent);

      this.#onSubmitSuccess(json);
    } catch (error) {
      this.#onSubmitError(error.message);
    }
  };

  #onSubmitError(error) {
    this.#enableForm();

    this.refs.addToCartTextError.textContent = error;
    this.refs.liveRegion.setAttribute('aria-live', 'assertive');
    this.refs.liveRegion.textContent = error;

    this.#timeout = setTimeout(() => {
      this.refs.liveRegion.setAttribute('aria-live', 'polite');
    }, ERROR_MESSAGE_DISPLAY_DURATION);

    const event = new CustomEvent(ThemeEvents.cartError, {
      detail: {
        source: 'product-form-component',
        sourceId: this.id,
        error: error
      }
    });
    document.dispatchEvent(event);
  }

  #onSubmitSuccess(cart) {
    this.#enableForm();

    const message = this.dataset.addToCartText;
    this.refs.liveRegion.setAttribute('aria-live', 'assertive');
    this.refs.liveRegion.textContent = message;

    this.#timeout = setTimeout(() => {
      this.refs.liveRegion.setAttribute('aria-live', 'polite');
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);

    const event = new CustomEvent(ThemeEvents.cartAdd, {
      detail: {
        source: 'product-form-component',
        sourceId: this.id,
        data: cart
      }
    });
    document.dispatchEvent(event);
  }

  #disableForm() {
    Array.from(this.elements).forEach((element) => {
      if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
        element.disabled = true;
      }
    });

    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    if (addToCartButtonContainer) {
      addToCartButtonContainer.disable();
    }
  }

  #enableForm() {
    Array.from(this.elements).forEach((element) => {
      if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
        element.disabled = false;
      }
    });

    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    if (addToCartButtonContainer) {
      addToCartButtonContainer.enable();
    }

    this.#timeout = setTimeout(() => {
      if (addToCartButtonContainer?.refs.addToCartButton?.disabled) {
        addToCartButtonContainer.enable();
      }
    }, ERROR_BUTTON_REENABLE_DELAY);
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      quantityLabel.textContent = cartQty;
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // ✅ SINCRONIZA IMAGEM AO ATUALIZAR VARIANTE
    setTimeout(() => {
      const variantName = getSelectedVariantName();
      if (variantName) {
        updateProductImage(variantName);
      }
    }, 150);

    const quantitySelector = this.querySelector('quantity-selector-component');
    const newQuantityInput = event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        const newQuantitySelector = this.querySelector('quantity-selector-component');
        const newQuantityInputElement = event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          newQuantitySelector.setValue(currentQuantityValue);
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    await this.#fetchAndUpdateCartQuantity();
  };

  #morphOrUpdateElement(currentElement, newElement, contextElement) {
    if (!currentElement || !newElement) return;
    morph(currentElement, newElement);
    contextElement?.updateConstraints?.();
  }

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}